import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, MapPin, ArrowLeft, ArrowRight } from "lucide-react";
import content from "@/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Spotlight() {
  const s = content.spotlight;
  const [selected, setSelected] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);

  const closeModal = () => {
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setSelected(null);
      setClosing(false);
    }, 280);
  };

  const open = (item) => {
    clearTimeout(closeTimer.current);
    setClosing(false);
    setImgIndex(0);
    setPaused(false);
    setSelected(item);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const imgs = selected ? (selected.images && selected.images.length ? selected.images : [selected.image]) : [];
  const stepImg = (d) => setImgIndex((p) => (p + d + imgs.length) % imgs.length);

  useEffect(() => {
    if (!selected || imgs.length < 2 || paused) return;
    const t = setInterval(() => setImgIndex((p) => (p + 1) % imgs.length), 7000);
    return () => clearInterval(t);
  }, [selected, imgs.length, paused]);

  useEffect(() => {
    if (!selected || imgs.length < 2) return;
    [imgs[(imgIndex + 1) % imgs.length], imgs[(imgIndex - 1 + imgs.length) % imgs.length]].forEach((src) => {
      const img = new Image();
      img.src = src;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, imgIndex]);

  return (
    <div id="spotlight" data-testid="spotlight-section" className="relative mt-16 lg:mt-20 border-t border-ink/10 pt-10 lg:pt-14">
      <div className="max-w-2xl mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-serif font-light text-4xl sm:text-5xl leading-tight"
            data-testid="spotlight-heading"
          >
            {s.heading}
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {s.items.map((item, i) => (
            <button
              key={item.id}
              data-testid={`spotlight-card-${i}`}
              onClick={() => open(item)}
              style={{ animationDelay: `${(i % 4) * 0.08}s` }}
              className="card-fade relative overflow-hidden group text-left aspect-[4/5] cursor-pointer"
            >
              <img
                src={(item.images && item.images[0]) || item.image}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                {item.category && (
                  <p className="text-[10px] uppercase tracking-[0.25em] text-goldsoft mb-2">{item.category}</p>
                )}
                <h3 className="font-serif text-xl lg:text-2xl text-linen font-light">{item.title}</h3>
                <p className="mt-1 text-[11px] text-linen/60 uppercase tracking-[0.15em]">{item.role}</p>
              </div>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-linen/90 text-ink text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                View
              </div>
            </button>
          ))}
        </div>

      {selected && (
        <div
          data-testid="spotlight-modal"
          className={`fixed inset-0 z-[90] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-8 transition-opacity duration-300 ${closing ? "opacity-0 pointer-events-none" : "opacity-100"}`}
          onClick={closeModal}
        >
          <div
            className="viewer-enter relative inline-block max-w-[94vw] max-h-[92vh] overflow-hidden shadow-2xl leading-none"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={() => setPaused(true)}
            onPointerUp={() => setPaused(false)}
            onPointerLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
          >
            <img
              key={imgs[imgIndex]}
              src={imgs[imgIndex]}
              alt={selected.title}
              className="img-fade block max-h-[92vh] max-w-[94vw] w-auto h-auto object-contain"
            />
            {imgs.length > 1 && (
              <>
                <div className="absolute top-1/2 -translate-y-1/2 inset-x-0 flex justify-between px-3 pointer-events-none">
                  <button
                    data-testid="spotlight-prev-btn"
                    onClick={(e) => { e.stopPropagation(); stepImg(-1); }}
                    className="w-10 h-10 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors pointer-events-auto"
                    aria-label="Previous photo"
                  >
                    <ArrowLeft size={15} />
                  </button>
                  <button
                    data-testid="spotlight-next-btn"
                    onClick={(e) => { e.stopPropagation(); stepImg(1); }}
                    className="w-10 h-10 bg-ink/60 backdrop-blur text-linen flex items-center justify-center rounded-full hover:bg-ink transition-colors pointer-events-auto"
                    aria-label="Next photo"
                  >
                    <ArrowRight size={15} />
                  </button>
                </div>
                <div className="absolute top-4 left-4 bg-ink/60 backdrop-blur text-linen text-[10px] tracking-[0.2em] px-3 py-1.5 rounded-full">
                  {imgIndex + 1} / {imgs.length}
                </div>
              </>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 via-ink/55 to-transparent px-5 sm:px-7 pt-14 pb-4 sm:pb-5">
              {selected.category && (
                <p className="text-[10px] uppercase tracking-[0.3em] text-goldsoft mb-1 truncate">{selected.category}</p>
              )}
              <div className="flex items-baseline justify-between gap-x-4 gap-y-1 min-w-0">
                <h3 className="font-serif font-light text-2xl sm:text-3xl leading-tight text-linen truncate min-w-0" data-testid="spotlight-modal-title">
                  {selected.title}
                </h3>
                <p className="text-[11px] text-linen/85 uppercase tracking-[0.15em] flex items-center gap-2 min-w-0 shrink">
                  <MapPin size={11} className="text-crimson shrink-0" />
                  <span className="truncate">{selected.role}{selected.location ? ` · ${selected.location}` : ""}</span>
                </p>
              </div>
            </div>
            <button
              data-testid="spotlight-modal-close"
              onClick={closeModal}
              className="absolute top-3 right-3 w-10 h-10 bg-linen text-ink flex items-center justify-center hover:bg-goldsoft transition-colors duration-300 z-10"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
